import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Label } from '@/components/ui/label';
import { SuperAdminOwnerDetailDto } from '@/features/superAdmin/features/data/types/superAdmin-data.types';
import {
    formatBoolean,
    formatNavValue,
} from '@/features/superAdmin/features/data/shared/utils/superAdminDataFormatters';
import { getOwnerStatusLabel } from '@/features/superAdmin/features/data/owners/utils/superAdminOwnerEnumLabels';

type DetailField = {
    label: string;
    value: string;
};

type Props = {
    owner: SuperAdminOwnerDetailDto;
};

const SuperAdminOwnerMainTab: FC<Props> = ({ owner }) => {
    const { t } = useTranslation();

    const fields: DetailField[] = [
        { label: t('superAdmin.data.columns.id'), value: owner.id },
        {
            label: t('superAdmin.data.columns.tenant'),
            value: formatNavValue(owner.tenantId, owner.tenantName),
        },
        { label: t('superAdmin.data.owners.columns.name'), value: owner.name },
        { label: t('superAdmin.data.owners.columns.email'), value: owner.email ?? '' },
        { label: t('superAdmin.data.owners.columns.phoneNumber'), value: owner.phoneNumber ?? '' },
        { label: t('superAdmin.data.owners.columns.tin'), value: owner.tin ?? '' },
        { label: t('superAdmin.data.owners.columns.language'), value: owner.language ?? '' },
        {
            label: t('superAdmin.data.columns.status'),
            value: getOwnerStatusLabel(owner.status, t),
        },
        { label: t('superAdmin.data.owners.columns.userId'), value: owner.userId ?? '' },
        { label: t('superAdmin.data.owners.columns.avatarUrl'), value: owner.avatarUrl ?? '' },
        { label: t('superAdmin.data.owners.columns.billingAddress'), value: owner.billingAddress ?? '' },
        { label: t('superAdmin.data.owners.columns.personalId'), value: owner.personalId ?? '' },
        {
            label: t('superAdmin.data.columns.deletionMark'),
            value: formatBoolean(owner.deletionMark),
        },
        { label: t('superAdmin.data.columns.externalId'), value: owner.externalId ?? '' },
    ];

    const permissionFields: DetailField[] = owner.permissions
        ? [
              {
                  label: t('superAdmin.data.owners.permissions.showCalendar'),
                  value: formatBoolean(owner.permissions.showCalendar),
              },
              {
                  label: t('superAdmin.data.owners.permissions.showFinances'),
                  value: formatBoolean(owner.permissions.showFinances),
              },
              {
                  label: t('superAdmin.data.owners.permissions.createBookings'),
                  value: formatBoolean(owner.permissions.createBookings),
              },
              {
                  label: t('superAdmin.data.owners.permissions.placeOrders'),
                  value: formatBoolean(owner.permissions.placeOrders),
              },
              {
                  label: t('superAdmin.data.owners.permissions.bookingDurationLimit'),
                  value: String(owner.permissions.bookingDurationLimit),
              },
              {
                  label: t('superAdmin.data.owners.permissions.yearlyBookingLimit'),
                  value: String(owner.permissions.yearlyBookingLimit),
              },
              {
                  label: t('superAdmin.data.owners.permissions.loginToAccount'),
                  value: formatBoolean(owner.permissions.loginToAccount),
              },
          ]
        : [];

    return (
        <div className="flex flex-col gap-6">
            <div className="rounded-semi-xl border border-popover-100 bg-popover-10 p-6">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    {fields.map((field) => (
                        <div key={field.label} className="flex flex-col gap-1.5">
                            <Label className="text-popover-800">{field.label}</Label>
                            <span className="break-all text-sm">{field.value || '—'}</span>
                        </div>
                    ))}
                </div>
            </div>
            {permissionFields.length > 0 && (
                <div className="rounded-semi-xl border border-popover-100 bg-popover-10 p-6">
                    <h3 className="mb-4 text-sm font-medium">{t('superAdmin.data.owners.permissions.title')}</h3>
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                        {permissionFields.map((field) => (
                            <div key={field.label} className="flex flex-col gap-1.5">
                                <Label className="text-popover-800">{field.label}</Label>
                                <span className="break-all text-sm">{field.value || '—'}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SuperAdminOwnerMainTab;
